import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Confirm } from "@/components/kids/kidsUi";
import { useHub } from "@/components/AppShell";
import { HOME_TYPES } from "@/components/HubForms";

const KIDS_WARN = "Your kids won't see Kids Corner. Chores, allowance and screen time will pause.";

function Switch({ s, disabled, onToggle }) {
  return (
    <button role="switch" aria-checked={s.on} disabled={disabled} onClick={onToggle}
      className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-xl px-3 text-left text-sm text-q-text hover:bg-q-raised disabled:opacity-60" data-testid={`section-switch-${s.key}`}>
      <span className="min-w-0 flex-1">{s.label}</span>
      <span className="relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors duration-150" style={{ background: s.on ? "rgb(var(--glow-family))" : "rgb(var(--q-line, 80 80 80))" }}>
        <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform duration-150" style={{ transform: `translateX(${s.on ? 22 : 2}px)` }} />
      </span>
    </button>
  );
}

export function TenurePicker({ hub, tenure, homeType, onSaved }) {
  const { reloadHub } = useHub();
  const save = (t, ht) => api.put(`/organizations/${hub.id}/home-profile`, { tenure: t, home_type: ht || null }).then(({ data }) => { onSaved?.(data); toast.success(t === "rent" ? "Lease, Move-in/out log and Maintenance are on" : "Housing costs is on"); reloadHub?.(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="tenure-picker">
      {[["rent", "Rent"], ["own", "Own"]].map(([k, l]) => <button key={k} className={`q-btn min-h-[44px] ${tenure === k ? "q-btn-primary" : "q-btn-secondary"}`} aria-pressed={tenure === k} disabled={hub.read_only} onClick={() => save(k, homeType)} data-testid={`tenure-${k}`}>{l}</button>)}
      <select className="q-input min-h-[44px]" aria-label="Home type" value={homeType || ""} disabled={!tenure || hub.read_only} onChange={(e) => save(tenure, e.target.value)} data-testid="home-type">
        <option value="">Home type</option>{HOME_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
    </div>
  );
}

function Household({ hub, d, onSaved }) {
  const h = d.household || { kids: d.kid_count > 0, pets: false, cars: false };
  const set = (k, v) => api.put(`/organizations/${hub.id}/household`, { kids: !!h.kids, pets: !!h.pets, cars: !!h.cars, [k]: v }).then(({ data }) => onSaved(data)).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="mt-4 border-t border-q-border pt-4" data-testid="household-answers">
      <p className="text-sm font-semibold text-q-text">Who's in your home?</p>
      <p className="text-xs text-q-muted">Saved for later: Pets and Vehicles arrive in a coming update.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {[["kids", "Kids"], ["pets", "Pets"], ["cars", "Cars"]].map(([k, l]) => (
          <button key={k} className={`q-btn min-h-[44px] ${h[k] ? "q-btn-primary" : "q-btn-secondary"}`} aria-pressed={!!h[k]} disabled={hub.read_only} onClick={() => set(k, !h[k])} data-testid={`household-${k}`}>{l}: {h[k] ? "Yes" : "No"}</button>
        ))}
      </div>
      <TenurePicker hub={hub} tenure={d.tenure} homeType={d.home_type} onSaved={(x) => onSaved({ ...d, ...x })} />
    </div>
  );
}

export default function FamilySections({ hub, onChanged }) {
  const [d, setD] = useState(null);
  const [ask, setAsk] = useState(false);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/sections`).then(({ data }) => setD(data)).catch(() => setD(false)), [hub.id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const flip = (s) => api.patch(`/organizations/${hub.id}/sections`, { section: s.key, on: !s.on }).then(({ data }) => { setD(data); onChanged(); toast.success(`${s.label.split(" (")[0]} ${s.on ? "turned off" : "turned on"}`); }).catch((e) => toast.error(errMsg(e)));
  const toggle = (s) => (s.key === "kids" && s.on && d.kid_count > 0 ? setAsk(s) : flip(s));
  return (
    <section className="q-card mt-6" data-testid="family-sections">
      <h2 className="font-display text-lg font-bold text-q-text">Family sections</h2>
      <p className="mt-1 text-sm text-q-muted">Hide whole sections your family doesn't use. Their data is kept. {d.always_on.join(", ")} are always on.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {d.groups.map((g) => (
          <div key={g.label} data-testid={`section-group-${g.label}`}>
            <p className="mb-1 text-[10px] font-black uppercase tracking-[0.16em] text-q-faint">{g.label}</p>
            {g.sections.map((s) => <Switch key={s.key} s={s} disabled={hub.read_only} onToggle={() => toggle(s)} />)}
          </div>
        ))}
      </div>
      <Household hub={hub} d={d} onSaved={setD} />
      <Confirm open={!!ask} onClose={() => setAsk(false)} title="Turn off Kids Corner?" body={KIDS_WARN} confirmLabel="Turn off" onConfirm={() => { flip(ask); setAsk(false); }} testid="kids-off-confirm" />
    </section>
  );
}
